import { usePopper } from "react-popper"
import { PropsWithChildren, useEffect, useState } from "react"
import { useClickAway, useToggle, useUnmount } from "react-use"
import cn from "classnames"
import { createPortal } from "react-dom"

import { useIsTablet } from "modules/PsyProfile/common/utils"
import { IconsName } from "components"
import { getMobileStyles } from "modules/Call/components/VideoControls"
import { MoreSelectorTooltip } from "modules/Call/components/HelpMoreMenuWrapper/MoreSelectorTooltip"
import { getOrCreatePortalNode } from "modules/Call/common/utils/getOrCreatePortalNode"
import style from "modules/Call/components/HelpMoreMenuWrapper/PopperTooltip/style.module.scss"

export type Item = {
    id: number
    icon: IconsName
    label: string
    description?: string
    action: () => void
}

interface Props {
    items: Item[]
    openTooltip?: (isOpen: boolean, tooltipRef: HTMLDivElement | null) => void
}

export function PopperTooltip({ children, items, openTooltip }: PropsWithChildren<Props>) {
    const isTablet = useIsTablet()
    const [showTooltip, setShowTooltip] = useToggle(false)
    const [referenceElement, setReferenceElement] = useState<HTMLDivElement | null>(null)
    const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null)
    const portalNode = getOrCreatePortalNode()
    useUnmount(() => portalNode.remove())

    useClickAway({ current: popperElement }, (event) => {
        if (event.target instanceof Node) {
            setShowTooltip(false)
        }
    })
    const offset = [114, 8] as [number, number]
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: "top",
        modifiers: [
            {
                name: "offset",
                options: {
                    offset,
                },
            },
        ],
    })
    const overrideStyles = !isTablet && styles.popper ? getMobileStyles(styles.popper) : undefined

    useEffect(() => {
        openTooltip && openTooltip(showTooltip, referenceElement)
    }, [showTooltip])

    return (
        <div ref={setReferenceElement} onClick={setShowTooltip}>
            <div>{children}</div>
            {createPortal(
                <MoreSelectorTooltip
                    ref={setPopperElement}
                    items={items}
                    className={cn(style.popper, {
                        [style.showTooltip]: showTooltip,
                    })}
                    style={{ ...styles.popper, ...overrideStyles }}
                    {...attributes.popper}
                />,
                portalNode
            )}
        </div>
    )
}
